import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from 'react-query';
import dashboardService from '../services/dashboardService';

// Generic API hook with loading, error, and data states
export function useApi(apiCall, dependencies = [], options = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const execute = useCallback(async (...args) => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await apiCall(...args);
      
      if (mountedRef.current) {
        setData(result);
        setLoading(false);
      }
      
      return result;
    } catch (err) {
      if (mountedRef.current) {
        setError(err);
        setLoading(false);
      }
      throw err;
    }
  }, [apiCall]);

  useEffect(() => {
    if (options.immediate !== false) {
      execute();
    }
  }, dependencies);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    data,
    loading,
    error,
    execute,
    refetch: execute,
  };
}

// Dashboard data hooks using React Query for better caching and synchronization
export function useDashboardData(options = {}) {
  return useQuery(
    'dashboardData',
    dashboardService.getDashboardData.bind(dashboardService),
    {
      staleTime: 1000 * 60 * 2, // 2 minutes
      cacheTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: true,
      refetchInterval: options.realTime ? 1000 * 30 : false, // 30 seconds if real-time
      ...options,
    }
  );
}

export function useDashboardMetrics(days = 30) {
  return useQuery(
    ['dashboardMetrics', days],
    () => dashboardService.getMetrics(days),
    {
      staleTime: 1000 * 60 * 1, // 1 minute
      cacheTime: 1000 * 60 * 3, // 3 minutes
    }
  );
}

export function useChartData(days = 30) {
  return useQuery(
    ['chartData', days],
    () => dashboardService.getChartData(days),
    {
      staleTime: 1000 * 60 * 2, // 2 minutes
      cacheTime: 1000 * 60 * 5, // 5 minutes
    }
  );
}

export function useRecentActivities(limit = 20) {
  return useQuery(
    ['recentActivities', limit],
    () => dashboardService.getRecentActivities(limit),
    {
      staleTime: 1000 * 30, // 30 seconds
      cacheTime: 1000 * 60 * 2, // 2 minutes
      refetchInterval: 1000 * 15, // 15 seconds
    }
  );
}

// Mutations for data updates
export function useRefreshDashboard() {
  const queryClient = useQueryClient();
  
  return useMutation(
    dashboardService.refreshDashboard.bind(dashboardService),
    {
      onSuccess: () => {
        // Invalidate all dashboard-related queries
        queryClient.invalidateQueries('dashboardData');
        queryClient.invalidateQueries('dashboardMetrics');
        queryClient.invalidateQueries('chartData');
        queryClient.invalidateQueries('recentActivities');
      },
    }
  );
}

// Advanced dashboard hook that combines multiple data sources
export function useAdvancedDashboard() {
  const [period, setPeriod] = useState('30d');
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  const dashboardQuery = useDashboardData({
    refetchInterval: autoRefresh ? 1000 * 30 : false,
  });
  
  const metricsQuery = useDashboardMetrics(
    period === '7d' ? 7 : period === '90d' ? 90 : 30
  );
  
  const chartQuery = useChartData(
    period === '7d' ? 7 : period === '90d' ? 90 : 30
  );
  
  const activitiesQuery = useRecentActivities(20);
  
  const refreshMutation = useRefreshDashboard();

  const isLoading = 
    dashboardQuery.isLoading || 
    metricsQuery.isLoading || 
    chartQuery.isLoading || 
    activitiesQuery.isLoading;

  const error = 
    dashboardQuery.error || 
    metricsQuery.error || 
    chartQuery.error || 
    activitiesQuery.error;

  const refetchAll = useCallback(() => {
    dashboardQuery.refetch();
    metricsQuery.refetch();
    chartQuery.refetch();
    activitiesQuery.refetch();
  }, [dashboardQuery, metricsQuery, chartQuery, activitiesQuery]);

  const refreshDashboard = useCallback(() => {
    refreshMutation.mutate();
  }, [refreshMutation]);

  return {
    // Data
    dashboardData: dashboardQuery.data,
    metrics: metricsQuery.data,
    chartData: chartQuery.data,
    activities: activitiesQuery.data,
    
    // State
    isLoading,
    error,
    isRefreshing: refreshMutation.isLoading,
    
    // Controls
    period,
    setPeriod,
    autoRefresh,
    setAutoRefresh,
    
    // Actions
    refetch: refetchAll,
    refresh: refreshDashboard,
  };
}

// Optimistic updates hook
export function useOptimisticUpdate(queryKey, updateFn) {
  const queryClient = useQueryClient();
  
  const update = useCallback((newData) => {
    queryClient.setQueryData(queryKey, (oldData) => {
      if (!oldData) return newData;
      return updateFn(oldData, newData);
    });
  }, [queryClient, queryKey, updateFn]);
  
  const rollback = useCallback(() => {
    queryClient.invalidateQueries(queryKey);
  }, [queryClient, queryKey]);
  
  return { update, rollback };
}

// Infinite query hook for paginated data
export function useInfiniteApi(queryKey, apiCall, options = {}) {
  const {
    getNextPageParam = (lastPage, pages) => {
      if (lastPage?.hasMore) {
        return pages.length;
      }
      return undefined;
    },
    ...queryOptions
  } = options;
  
  return useInfiniteQuery(
    queryKey,
    ({ pageParam = 0 }) => apiCall(pageParam),
    {
      getNextPageParam,
      staleTime: 1000 * 60 * 2,
      ...queryOptions,
    }
  );
}

// Real-time data synchronization hook
export function useRealTimeSync(queryKeys = []) {
  const queryClient = useQueryClient();
  
  useEffect(() => {
    // Listen for real-time updates
    const handleDashboardRefresh = () => {
      queryKeys.forEach(key => {
        queryClient.invalidateQueries(key);
      });
    };
    
    const handleWidgetUpdate = () => {
      queryClient.invalidateQueries('dashboardData');
    };
    
    window.addEventListener('dashboard:refresh', handleDashboardRefresh);
    window.addEventListener('widget:updated', handleWidgetUpdate);
    
    return () => {
      window.removeEventListener('dashboard:refresh', handleDashboardRefresh);
      window.removeEventListener('widget:updated', handleWidgetUpdate);
    };
  }, [queryClient, queryKeys]);
}

// Background sync hook for offline support
export function useBackgroundSync() {
  const queryClient = useQueryClient();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Refetch all queries when back online
      queryClient.refetchQueries();
    };
    
    const handleOffline = () => {
      setIsOnline(false);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [queryClient]);
  
  return { isOnline };
}

// Performance monitoring hook
export function useApiPerformance() {
  const [metrics, setMetrics] = useState({
    averageResponseTime: 0,
    slowQueries: [],
    errorRate: 0,
  });
  
  const queryClient = useQueryClient();
  
  useEffect(() => {
    const cache = queryClient.getQueryCache();
    
    const calculateMetrics = () => {
      const queries = cache.getAll();
      const recentQueries = queries.filter(
        query => query.state.dataUpdatedAt > Date.now() - 1000 * 60 * 5 // Last 5 minutes
      );
      
      if (recentQueries.length === 0) return;
      
      const responseTimes = recentQueries
        .map(q => q.state.dataUpdatedAt - q.state.fetchMeta?.startTime)
        .filter(Boolean);
      
      const avgResponseTime = responseTimes.length > 0
        ? responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length
        : 0;
      
      const errors = recentQueries.filter(q => q.state.error);
      const errorRate = recentQueries.length > 0
        ? (errors.length / recentQueries.length) * 100
        : 0;
      
      const slowQueries = recentQueries
        .filter(q => {
          const responseTime = q.state.dataUpdatedAt - q.state.fetchMeta?.startTime;
          return responseTime > 2000; // Slower than 2 seconds
        })
        .map(q => q.queryKey);
      
      setMetrics({
        averageResponseTime: avgResponseTime,
        slowQueries,
        errorRate,
      });
    };
    
    const interval = setInterval(calculateMetrics, 30000); // Every 30 seconds
    
    return () => clearInterval(interval);
  }, [queryClient]);
  
  return metrics;
}